import type { EChartsOption } from 'echarts';
import type { Municipality } from '@/types';

/** A municipality that actually carries a centroid — the only kind the map can plot. */
export type PlottableMunicipality = Municipality & { lat: number; lng: number };

export function isPlottable(city: Municipality | undefined): city is PlottableMunicipality {
  return city?.lat !== undefined && city.lng !== undefined;
}

/**
 * The window the map opens on: every point it will draw, plus breathing room.
 *
 * <p>Never a fixed UF. A purchase and the company behind it are often in different states,
 * so framing on either one would push the other off the canvas — and with a single point
 * there is no state to frame on at all, only a city. Padding scales with the span so two
 * neighbouring cities do not render as a hairline, and the floor gives a lone city a
 * region around it instead of a full-bleed dot.</p>
 */
export function frameFor(
  cities: readonly PlottableMunicipality[],
): [[number, number], [number, number]] | null {
  if (cities.length === 0) return null;

  const lngs = cities.map((c) => c.lng);
  const lats = cities.map((c) => c.lat);
  const west = Math.min(...lngs);
  const east = Math.max(...lngs);
  const south = Math.min(...lats);
  const north = Math.max(...lats);
  const pad = Math.max(1.2, (east - west) * 0.35, (north - south) * 0.35);

  return [
    [west - pad, north + pad],
    [east + pad, south - pad],
  ];
}

/**
 * The company's point, but only when it is genuinely ELSEWHERE. Same city means one dot,
 * not two stacked on each other pretending to be a journey.
 */
export function companyPointFor(
  purchase: PlottableMunicipality,
  address: Municipality | undefined,
): PlottableMunicipality | null {
  return isPlottable(address) && address.ibgeCode !== purchase.ibgeCode ? address : null;
}

/** What the map says to a screen reader, which cannot see either dot. */
export function locationMapDescription(
  purchase: PlottableMunicipality,
  company: PlottableMunicipality | null,
): string {
  return company
    ? `Mapa: compra em ${purchase.name} · ${purchase.uf}, empresa registrada em ${company.name} · ${company.uf}.`
    : `Mapa: compra em ${purchase.name} · ${purchase.uf}.`;
}

export interface LocationMapColors {
  purchase: string;
  company: string;
  land: string;
  landBorder: string;
  labelText: string;
  labelBackground: string;
}

/**
 * The chart option, built as data so the one-point / two-point decision is testable
 * without rendering a canvas.
 */
export function buildLocationMapOption(
  mapName: string,
  purchase: PlottableMunicipality,
  address: Municipality | undefined,
  colors: LocationMapColors,
): EChartsOption | null {
  const company = companyPointFor(purchase, address);
  const frame = frameFor(company ? [purchase, company] : [purchase]);
  if (!frame) return null;

  // With two points the label carries a caption line saying WHICH fact the dot is —
  // colour alone cannot (VER-358). A lone point keeps the plain city name: there is
  // nothing to tell apart.
  const label = (text: string, caption: string | null, position: 'top' | 'bottom') => ({
    show: true,
    position,
    formatter: caption ? `{n|${text}}\n{c|${caption}}` : text,
    color: colors.labelText,
    fontSize: 10,
    backgroundColor: colors.labelBackground,
    padding: [2, 4] as [number, number],
    borderRadius: 3,
    ...(caption
      ? {
          rich: {
            n: { color: colors.labelText, fontSize: 10, fontWeight: 600 as const, lineHeight: 13 },
            c: { color: colors.labelText, fontSize: 8, opacity: 0.7, lineHeight: 10 },
          },
        }
      : {}),
  });

  const point = (
    city: PlottableMunicipality,
    color: string,
    caption: string | null,
    position: 'top' | 'bottom',
    size: number,
  ) => ({
    type: 'scatter' as const,
    coordinateSystem: 'geo' as const,
    symbolSize: size,
    itemStyle: { color },
    label: label(city.name, caption, position),
    data: [{ value: [city.lng, city.lat] }],
    zlevel: 3,
  });

  return {
    backgroundColor: 'transparent',
    animation: false,
    geo: {
      map: mapName,
      // No roam: the resolution is municipal, so panning and zooming would only invite the
      // user to look for a precision that is not there.
      roam: false,
      silent: true,
      boundingCoords: frame,
      itemStyle: {
        areaColor: colors.land,
        borderColor: colors.landBorder,
        borderWidth: 0.6,
      },
    },
    series: [
      ...(company
        ? [
            {
              type: 'lines' as const,
              coordinateSystem: 'geo' as const,
              data: [{ coords: [[purchase.lng, purchase.lat], [company.lng, company.lat]] }],
              lineStyle: { color: colors.purchase, width: 1.4, curveness: 0.25, opacity: 0.9 },
              zlevel: 2,
            },
            point(company, colors.company, 'onde o CNPJ está', 'top', 8),
          ]
        : []),
      point(purchase, colors.purchase, company ? 'onde você comprou' : null, 'bottom', 9),
    ],
  };
}
